/**
 * ChatTermSheetCard — T-36 get_latest_term_sheet tool result.
 *
 * Compact provenance card: filename, bank, upload date, version badge, status.
 * ~72px height. Three status states: analyzed / error / processing.
 * Bank name omitted when status != ANALYZED (not yet extracted from PDF).
 *
 * Design system: no rounded-* classes (except rounded-full on the status dot —
 * circular indicator exception, same as ConfidenceDot).
 */
import { AlertCircle, File as FileIcon } from 'react-feather';
import { Spinner } from '../../../components/ui';

import { clsx } from 'clsx';

import type { ToolResultProps } from '../types';

interface TermSheetResult {
  available: boolean;
  term_sheet_id?: number;
  filename?: string;
  uploaded_at?: string;
  version_number?: number;
  version_label?: string;
  status?: string;
  bank_name?: string | null;
  deal_id?: number;
  deal_name?: string;
  reason?: string;
}

const STATUS_CONFIG: Record<
  string,
  { dot: string; label: string; labelColor: string; icon?: 'spinner' | 'alert' }
> = {
  ANALYZED: {
    dot: 'bg-success',
    label: 'Analyzed',
    labelColor: 'text-success',
  },
  ERROR: {
    dot: 'bg-error',
    label: 'Error',
    labelColor: 'text-error',
    icon: 'alert',
  },
  UPLOADING: {
    dot: 'bg-warning',
    label: 'Uploading',
    labelColor: 'text-warning',
    icon: 'spinner',
  },
  UPLOADED: {
    dot: 'bg-warning',
    label: 'Processing',
    labelColor: 'text-warning',
    icon: 'spinner',
  },
  ANALYZING: {
    dot: 'bg-warning',
    label: 'Analyzing',
    labelColor: 'text-warning',
    icon: 'spinner',
  },
};

function formatUploadDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-BE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function ChatTermSheetCard({ result }: ToolResultProps) {
  const r = (result ?? {}) as Partial<TermSheetResult>;

  if (!r.available || !r.filename) {
    return (
      <div className="mt-2 border border-border bg-surface-subtle px-4 py-3 text-body-sm text-txt-secondary">
        No term sheets found. Upload one to get started.
      </div>
    );
  }

  const config =
    STATUS_CONFIG[r.status ?? ''] ??
    ({ dot: 'bg-txt-tertiary', label: r.status ?? 'Unknown', labelColor: 'text-txt-tertiary' } as (typeof STATUS_CONFIG)[string]);

  const isSpinner = config.icon === 'spinner';
  const isAlert = config.icon === 'alert';

  const caption = [r.bank_name, r.deal_name].filter(Boolean).join(' · ');

  return (
    <div className="mt-2 flex items-center gap-3 border border-border bg-surface-card px-4 py-3">
      <div className="relative flex-shrink-0">
        <FileIcon size={20} className="text-txt-tertiary" />
        <span
          className={clsx(
            'absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-1 ring-surface-card',
            config.dot,
          )}
          aria-hidden="true"
        />
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-body-sm font-semibold text-txt-primary" title={r.filename}>
          {r.filename}
        </p>
        {caption && (
          <p className="mt-0.5 truncate text-caption text-txt-secondary">{caption}</p>
        )}
      </div>

      <div className="flex flex-shrink-0 flex-col items-end gap-0.5">
        <div className="flex items-center gap-1.5">
          {r.version_label && (
            <span className="text-overline uppercase text-txt-tertiary">
              {r.version_label}
            </span>
          )}
          <span className={clsx('flex items-center gap-0.5 text-overline uppercase', config.labelColor)}>
            {isSpinner && (
              <Spinner size={10} decorative delayMs={0} />
            )}
            {isAlert && (
              <AlertCircle size={10} aria-hidden="true" />
            )}
            {config.label}
          </span>
        </div>
        {r.uploaded_at && (
          <span className="text-caption text-txt-tertiary">
            {formatUploadDate(r.uploaded_at)}
          </span>
        )}
      </div>
    </div>
  );
}
